import {
  collection,
  doc,
  getDocs,
  getDoc,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { getFirebaseWebFirestore } from "./firebaseWeb";

const USER_PRIVATE_PROFILE_COLLECTION = "webUserPrivateProfiles";
const PENDING_FRIEND_REQUESTS_COLLECTION = "pendingFriendRequests";
const POST_LIKES_COLLECTION = "webPostLikes";
const POST_COMMENTS_COLLECTION = "webPostComments";
const EVENT_INVITATIONS_COLLECTION = "webEventInvitations";
const DEFAULT_NOTIFICATION_PREFERENCES = {
  preferredTimeMatches: true,
  friendHostedEvents: true,
  lastMinuteChanceEvents: true,
  socialUpdates: true,
};

function toDateValue(value) {
  return value?.toDate?.() || (value ? new Date(value) : null);
}

function timestampMs(value) {
  const date = toDateValue(value);
  return date && Number.isFinite(date.getTime()) ? date.getTime() : 0;
}

function relativeTime(value) {
  const ms = timestampMs(value);
  if (!ms) return "Zaman bilgisi yok";
  const diff = Math.max(0, Date.now() - ms);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return "Az önce";
  if (diff < hour) return `${Math.floor(diff / minute)} dakika önce`;
  if (diff < day) return `${Math.floor(diff / hour)} saat önce`;
  return `${Math.floor(diff / day)} gün önce`;
}

function mapFriendRequest(row) {
  return {
    id: `friend-${row.id}`,
    sourceId: row.id,
    type: "friend-request",
    category: "Arkadaşlık İstekleri",
    title: `${row.from?.displayName || row.from?.uid || "Bir kullanıcı"} arkadaşlık isteği gönderdi.`,
    body: row.requiresApproval ? "Bu isteği onaylayabilir veya reddedebilirsin." : "Arkadaşlık isteği tamamlandı.",
    meta: relativeTime(row.createdAt),
    createdAt: row.createdAt,
    status: row.status || "pending",
    actionable: row.status === "pending",
    request: row,
  };
}

function mapPostLike(row) {
  return {
    id: `like-${row.id}`,
    sourceId: row.id,
    type: "post-like",
    category: "Post Beğenileri",
    title: `${row.displayName || "Bir kullanıcı"} postunu beğendi.`,
    body: row.postTitle || row.postId || "Post beğenisi",
    meta: relativeTime(row.createdAt),
    createdAt: row.createdAt,
    status: "info",
  };
}

function mapPostComment(row) {
  return {
    id: `comment-${row.id}`,
    sourceId: row.id,
    type: "post-comment",
    category: "Post Yorumları",
    title: `${row.author?.displayName || row.authorName || "Bir kullanıcı"} postuna yorum yaptı.`,
    body: row.body || "Yeni yorum",
    meta: relativeTime(row.createdAt),
    createdAt: row.createdAt,
    status: "info",
  };
}

function mapEventInvitation(row) {
  return {
    id: `event-invite-${row.id}`,
    sourceId: row.id,
    type: "event-invitation",
    category: "Etkinlik Davetleri",
    title: `${row.from?.displayName || row.host?.displayName || "Bir kullanıcı"} seni etkinliğe davet etti.`,
    body: row.eventTitle || row.title || "Etkinlik daveti",
    meta: relativeTime(row.createdAt),
    createdAt: row.createdAt,
    status: row.status || "pending",
  };
}

async function listByTarget({ collectionName, uid, field = "targetUid", max = 40, mapper }) {
  const db = getFirebaseWebFirestore();
  const snapshot = await getDocs(query(
    collection(db, collectionName),
    where(field, "==", uid),
    limit(Math.max(1, Math.min(Number(max) || 40, 100))),
  ));
  return snapshot.docs.map((entry) => mapper({ id: entry.id, ...(entry.data() || {}) }));
}

export async function listUserNotifications({ uid, max = 80 } = {}) {
  if (!uid) return [];
  const [friendRequests, postLikes, postComments, eventInvitations] = await Promise.all([
    listByTarget({ collectionName: PENDING_FRIEND_REQUESTS_COLLECTION, uid, field: "to.uid", max, mapper: mapFriendRequest }),
    listByTarget({ collectionName: POST_LIKES_COLLECTION, uid, max, mapper: mapPostLike }),
    listByTarget({ collectionName: POST_COMMENTS_COLLECTION, uid, max, mapper: mapPostComment }),
    listByTarget({ collectionName: EVENT_INVITATIONS_COLLECTION, uid, field: "toUid", max, mapper: mapEventInvitation }),
  ]);

  return [...friendRequests, ...postLikes, ...postComments, ...eventInvitations]
    .sort((a, b) => timestampMs(b.createdAt) - timestampMs(a.createdAt))
    .slice(0, Math.max(1, Math.min(Number(max) || 80, 160)));
}

export async function getUnreadNotificationCount({ uid } = {}) {
  const rows = await listUserNotifications({ uid, max: 80 });
  return rows.filter((row) => row.status === "pending" || row.type === "post-like" || row.type === "post-comment").length;
}

export async function getNotificationPreferences({ uid } = {}) {
  if (!uid) return DEFAULT_NOTIFICATION_PREFERENCES;
  const db = getFirebaseWebFirestore();
  const snapshot = await getDoc(doc(db, USER_PRIVATE_PROFILE_COLLECTION, uid));
  const source = snapshot.exists() ? snapshot.data()?.preferenceProfile?.notificationPreferences : {};
  return {
    preferredTimeMatches: source?.preferredTimeMatches !== false,
    friendHostedEvents: source?.friendHostedEvents !== false,
    lastMinuteChanceEvents: source?.lastMinuteChanceEvents !== false,
    socialUpdates: source?.socialUpdates !== false,
  };
}

export async function saveNotificationPreferences({ uid, preferences } = {}) {
  if (!uid) throw new Error("Bildirim ayarları için giriş yapmalısın.");
  const db = getFirebaseWebFirestore();
  const nextPreferences = {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...(preferences || {}),
  };
  await setDoc(doc(db, USER_PRIVATE_PROFILE_COLLECTION, uid), {
    uid,
    preferenceProfile: {
      notificationPreferences: nextPreferences,
    },
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return nextPreferences;
}
